import React, { useEffect, useMemo, useRef } from 'react';
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import TileLayer from 'ol/layer/Tile.js';
import { XYZ } from 'ol/source';
import { fromLonLat, transformExtent } from 'ol/proj.js';
import { MapEdges } from './MapEdges';
import { MapNodes } from './MapNodes';
import { MapInterconnects } from './MapInterconnects';
import { MapChains } from './MapChains';
import { ZoomPath } from '../paths/ZoomPath';
import { MapContracts } from './MapContracts';
import { MapCounties } from './MapCounties';
import { useSelector } from 'react-redux';
import {
  MapMajorPointConstraints,
  MapMajorSegmentConstraints,
  MapMinorSegmentConstraints,
  MapMinorPointConstraints,
} from './MapConstraints';
import { MapMarkets } from './MapMarkets';
import { MapFlows } from './MapFlows';
import { MapNotices } from './MapNotices';
import { boundingExtent } from 'ol/extent';
import { MapRisePaths } from './MapRisePaths';
import { MapOperationMarkers } from './MapOperationMarkers';
import { MapOverlays } from './MapOverlays';
import process from 'process';
import { MapAllPathsChains } from './MapAllPathsChains';
import { useTheme } from '../helpers/hooks';
import { MapIOCPaths } from './MapIOCPaths';
import { MapEditor } from './MapEditor';
import { useLocation } from 'react-router-dom';
import { selectAllNodes, selectIsMapLoading } from './slice/mapSelectors';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { MapCapacityComponents } from './MapCapacityComponents';
import {
  selectFocusedNode,
  selectLayers,
  selectMapFit,
  selectMapView,
} from '../plotter/plotterSelectors';
import { MapHighlights } from './MapHighlights';
import { MapNoticeComponents } from './MapNoticeComponents';
import { FeatureLike } from 'ol/Feature';
import BaseEvent from 'ol/events/Event';
import { MapPaths } from './MapPaths';
import { MapPriceComponents } from './MapPriceComponents';
import { MapLabels } from './MapLabels';
import { ErrorBoundary } from '../helpers/ErrorBoundary';
import { useMapContext } from './context/MapContext';

export type TargetFeature = { feature: FeatureLike; zIndex: number };

/**
 *                       (x2,y2)
 *
 *
 *
 * (x1,y1)
 *
 * Coordinates in transfromExtent are [x1, y1, x2, y2]
 */
const extent = transformExtent([-150, 0, -45, 60], 'EPSG:4326', 'EPSG:3857');

const tileSources = {
  dark: {
    normal: new XYZ({
      attributions:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      url: `https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`,
    }),
    nolabels: new XYZ({
      attributions:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      url: `https://basemaps.cartocdn.com/rastertiles/dark_nolabels/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`,
    }),
  },
  light: {
    normal: new XYZ({
      attributions:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      url: `https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`,
    }),
    nolabels: new XYZ({
      attributions:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      url: `https://basemaps.cartocdn.com/rastertiles/light_nolabels/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`,
    }),
  },
};

const temperatureTileSource = new XYZ({
  url: `https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=${process.env.REACT_APP_WEATHER_MAPS_API_KEY}`,
});

const precipitationTileSource = new XYZ({
  url: `https://tile.openweathermap.org/map/precipitation_new/{z}/{x}/{y}.png?appid=${process.env.REACT_APP_WEATHER_MAPS_API_KEY}`,
});

export const EdgeMap = () => {
  const [theme] = useTheme();
  const {
    map,
    setMap,
    cursorStyle,
    setCursorStyle,
    hoverListeners,
    clickListeners,
  } = useMapContext();
  const location = useLocation();

  const isMapBuilderPage = location.pathname.startsWith('/map-builder');

  const containerRef = useRef<HTMLDivElement | null>(null);

  const mapView = useSelector(selectMapView);
  const focusedNode = useSelector(selectFocusedNode);
  const nodes = useSelector(selectAllNodes);
  const mapFit = useSelector(selectMapFit);
  const isMapLoading = useSelector(selectIsMapLoading);
  const layers = useSelector(selectLayers);

  const hoveredFeature = useRef<FeatureLike | null>(null);
  const clickedFeature = useRef<FeatureLike | null>(null);

  const tileLayer = useMemo(() => {
    const type = layers.counties ? 'nolabels' : 'normal';
    return new TileLayer({
      properties: { isBaseLayer: true },
      source: tileSources[theme][type],
    });
  }, []);

  useEffect(() => {
    const _map = new Map({
      layers: [tileLayer],
      view: new View({
        // projection: 'EPSG:3857' // This is the default projection for web maps (mercator),
        center: fromLonLat([-87, 33]),
        extent: extent,
        zoom: 4,
      }),
      target: containerRef.current ?? undefined,
    });
    const resizeHandler = () => _map.updateSize();
    containerRef.current?.addEventListener('resize', resizeHandler);
    setMap(_map);

    return () => {
      containerRef.current?.removeEventListener('resize', resizeHandler);
      _map.getAllLayers().forEach((layer) => layer.dispose());
      _map.dispose();
    };
  }, [containerRef]);

  useEffect(() => {
    if (!map) return;
    map
      .getLayers()
      .getArray()
      .filter((layer) => layer.get('key') === 'temperature')
      .forEach((layer) => {
        map.removeLayer(layer);
      });
    if (layers.temperature) {
      map.addLayer(
        new TileLayer({
          properties: { isExtraLayer: true, key: 'temperature' },
          source: temperatureTileSource,
        }),
      );
    }
    map.render();
  }, [layers.temperature, map]);

  useEffect(() => {
    if (!map) return;
    map
      .getLayers()
      .getArray()
      .filter((layer) => layer.get('key') === 'precipitation')
      .forEach((layer) => {
        map.removeLayer(layer);
      });
    if (layers.precipitation) {
      map.addLayer(
        new TileLayer({
          properties: { isExtraLayer: true, key: 'precipitation' },
          source: precipitationTileSource,
        }),
      );
    }
    map.render();
  }, [map, layers.precipitation]);

  useEffect(() => {
    if (!map) return;
    map.getLayers().forEach((layer) => {
      if (layer instanceof TileLayer && layer.get('isBaseLayer')) {
        const type = layers.counties ? 'nolabels' : 'normal';
        layer.setSource(tileSources[theme][type]);
      }
    });
    map.render();
  }, [theme, layers.counties]);

  // Set listeners
  useEffect(() => {
    if (!map) return;

    const hoverCallback = (_evt: BaseEvent | Event) => {
      const evt = _evt as MapBrowserEvent<PointerEvent>;
      const pixel = map.getEventPixel(evt.originalEvent);
      const features: TargetFeature[] = [];
      try {
        map.forEachFeatureAtPixel(
          pixel,
          (feature, layer) => {
            features.push({ feature, zIndex: layer.getZIndex() ?? 0 });
            return false;
          },
          {
            layerFilter: (layer) =>
              layer &&
              map.getAllLayers().includes(layer) &&
              layer.getProperties()?.interactable !== false,
          },
        );
      } catch (e) {
        if (process.env.WALL === 'true') {
          console.warn('forEachFeatureAtPixel: layer verification failed', e);
        }
      }
      const feature = features.sort((a, b) => b.zIndex - a.zIndex)[0]?.feature;

      if (feature) {
        map.getTargetElement().style.cursor = 'pointer';
      } else if (evt.dragging) {
        map.getTargetElement().style.cursor = 'grabbing';
      } else {
        map.getTargetElement().style.cursor = cursorStyle?.current ?? 'grab';
      }

      if (evt.dragging) {
        return;
      }

      if (
        feature?.getProperties()?.key ==
        hoveredFeature.current?.getProperties()?.key
      ) {
        return;
      }
      hoverListeners.current?.forEach((listener) => listener(feature, pixel));
      hoveredFeature.current = feature;
    };

    const clickCallback = (_evt: BaseEvent | Event) => {
      const evt = _evt as MapBrowserEvent<PointerEvent>;
      const pixel = map.getEventPixel(evt.originalEvent);
      const features: TargetFeature[] = [];
      map.forEachFeatureAtPixel(
        pixel,
        (feature, layer) => {
          if (!layer || !feature) return false;
          features.push({ feature, zIndex: layer.getZIndex() ?? 0 });
          return false;
        },
        {
          layerFilter: (layer) =>
            layer &&
            map.getAllLayers().includes(layer) &&
            layer.getProperties()?.interactable !== false,
        },
      );
      const feature = features.sort((a, b) => b.zIndex - a.zIndex)[0]?.feature;
      clickListeners.current?.forEach((listener) => {
        listener(feature, evt.coordinate);
      });
      clickedFeature.current = feature;
    };

    map.on(['pointermove'], hoverCallback);
    map.on(['click'], clickCallback);

    return () => {
      map.un(['pointermove'], hoverCallback);
      map.un(['click'], clickCallback);
    };
  }, [
    map,
    hoverListeners,
    clickListeners,
    setCursorStyle,
    cursorStyle,
    containerRef,
    clickedFeature,
    hoveredFeature,
  ]);

  useEffect(() => {
    if (mapView && map) {
      map.getView().animate({
        center: [mapView.center[0], mapView.center[1]],
        zoom: mapView.zoom,
        duration: 1000,
      });
    }
  }, [mapView]);

  useEffect(() => {
    if (map && mapFit) {
      if (mapFit.length < 2) {
        console.warn('mapFit requires at least 2 coordinates to fit the view');
        return;
      }
      map.getView().fit(boundingExtent(mapFit), {
        padding: [50, 50, 50, 50],
        duration: 1000,
      });
    }
  }, [mapFit]);

  useEffect(() => {
    if (!focusedNode || !map) return;
    const node = nodes[focusedNode.nodeId];
    if (!node) return;
    map.getView().animate({
      center: fromLonLat([node.lng, node.lat].map(Number)),
      zoom: focusedNode.zoom,
      duration: 1000,
    });
  }, [focusedNode]);

  map?.updateSize();

  let zIndex = 0;

  return (
    <div className="flex-grow-1" id="map-container" ref={containerRef}>
      <ZoomPath />
      <ErrorBoundary name="Map Overlays" silent>
        <MapOverlays zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Counties" silent>
        <MapCounties zIndex={++zIndex} />
      </ErrorBoundary>
      {isMapBuilderPage && (
        <>
          <ErrorBoundary name="Map Capacity Components" silent>
            <MapCapacityComponents zIndex={++zIndex} />
          </ErrorBoundary>
          <ErrorBoundary name="Map Price Components" silent>
            <MapPriceComponents zIndex={++zIndex} />
          </ErrorBoundary>
        </>
      )}
      <ErrorBoundary name="Map Rise Paths" silent>
        <MapRisePaths zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map IOC Paths" silent>
        <MapIOCPaths zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Contracts" silent>
        <MapContracts zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Edges" silent>
        <MapEdges zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Interconnects" silent>
        <MapInterconnects zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Highlights" silent>
        <MapHighlights zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Paths" silent>
        <MapPaths zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Chains" silent>
        <MapChains zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map All Paths Chains" silent>
        <MapAllPathsChains zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Operation Markers" silent>
        <MapOperationMarkers zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Markets" silent>
        <MapMarkets zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Minor Segment Constraints" silent>
        <MapMinorSegmentConstraints zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Major Segment Constraints" silent>
        <MapMajorSegmentConstraints zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Flows" silent>
        <MapFlows zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Nodes" silent>
        <MapNodes zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Minor Point Constraints" silent>
        <MapMinorPointConstraints zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Major Point Constraints" silent>
        <MapMajorPointConstraints zIndex={++zIndex} />
      </ErrorBoundary>
      <ErrorBoundary name="Map Notices" silent>
        <MapNotices zIndex={++zIndex} />
      </ErrorBoundary>
      {isMapBuilderPage && (
        <ErrorBoundary name="Map Notice Components" silent>
          <MapNoticeComponents zIndex={++zIndex} />
        </ErrorBoundary>
      )}
      {isMapBuilderPage && isMapLoading === false && (
        <>
          <ErrorBoundary name="Map Editor" silent>
            <MapEditor zIndex={++zIndex} />
          </ErrorBoundary>
        </>
      )}
      <ErrorBoundary name="Map Labels" silent>
        <MapLabels zIndex={++zIndex} />
      </ErrorBoundary>
    </div>
  );
};
